import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { initializeAppCheck, ReCaptchaV3Provider, getToken } from 'firebase/app-check';
import { getFirebaseConfig, validateConfig, getRecaptchaSiteKey } from './firebase-config.js';

// Load Firebase configuration from runtime config (production) or env vars (development)
const firebaseConfig = getFirebaseConfig();

// Validate configuration
const validation = validateConfig();
if (!validation.isValid) {
  console.error('❌ Missing Firebase configuration keys:', validation.missingKeys);
  console.error('');
  console.error('For development:');
  console.error('  1. Copy .env.example to .env');
  console.error('  2. Fill in your Firebase credentials from Firebase Console');
  console.error('');
  console.error('For production deployment:');
  console.error('  1. Ensure .env file exists with Firebase credentials');
  console.error('  2. Run: npm run build (this generates public/config.js)');
  console.error('  3. Run: firebase deploy');
  console.error('');
  throw new Error(`Firebase configuration incomplete. Missing: ${validation.missingKeys.join(', ')}`);
}

console.log('✅ Firebase configuration loaded successfully');
console.log(`   Project: ${firebaseConfig.projectId}`);

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize App Check with reCAPTCHA v3
// ✅ SECURE: Protects against bots and abuse
// Add your reCAPTCHA v3 SITE KEY to .env as VITE_RECAPTCHA_SITE_KEY
const recaptchaSiteKey = getRecaptchaSiteKey();

// Debug logging to help diagnose reCAPTCHA issues
console.log('🔍 App Check Debug:');
console.log('   reCAPTCHA Site Key present:', !!recaptchaSiteKey);
console.log('   Site Key (first 10 chars):', recaptchaSiteKey ? recaptchaSiteKey.substring(0, 10) + '...' : 'MISSING');
console.log('   Current domain:', window.location.hostname);
console.log('   Expected domain in reCAPTCHA:', 'trip-planner-claude.web.app');

if (recaptchaSiteKey) {
  try {
    const appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaV3Provider(recaptchaSiteKey),
      isTokenAutoRefreshEnabled: true
    });
    console.log('✅ App Check initialized successfully');
    console.log('   Provider:', 'ReCaptchaV3Provider');
    console.log('   Auto-refresh enabled:', true);

    // Try to get a token immediately to test
    // Note: getToken is async and may take a moment for reCAPTCHA to initialize
    setTimeout(() => {
      getToken(appCheck, false).then((result) => {
        console.log('✅ App Check token obtained successfully');
        console.log('   Token:', result.token.substring(0, 20) + '...');
      }).catch((error) => {
        console.error('❌ App Check token generation failed:', error);
        console.error('   Error code:', error.code);
        console.error('   Error message:', error.message);
        console.error('');
        console.error('🔍 Troubleshooting steps:');
        console.error('   1. Verify SITE KEY in .env matches reCAPTCHA admin console');
        console.error('   2. Verify SECRET KEY in Firebase App Check matches reCAPTCHA admin console');
        console.error('   3. Ensure both keys are from the SAME reCAPTCHA site');
        console.error('   4. Check that', window.location.hostname, 'is in reCAPTCHA allowed domains');
      });
    }, 2000); // Wait 2 seconds for reCAPTCHA to initialize
  } catch (error) {
    console.error('❌ App Check initialization failed:', error);
    console.error('   This usually means the reCAPTCHA provider could not be created');
  }
} else {
  console.warn('⚠️ App Check not initialized: VITE_RECAPTCHA_SITE_KEY missing');
  console.warn('   Add VITE_RECAPTCHA_SITE_KEY to your .env file');
}

// Initialize Firebase services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

// Connect to emulators if in development mode
const env = (typeof process !== 'undefined' && process.env) || import.meta.env || {};
const isLocalhost = typeof window !== 'undefined' && window.location && window.location.hostname === 'localhost';
if ((env.DEV || isLocalhost) && import.meta.env.VITE_USE_EMULATORS !== 'false') {
  console.log('🔧 Connecting to Firebase Emulators...');

  try {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    console.log('✅ Auth emulator connected (port 9099)');
  } catch (e) {
    console.log('⚠️ Auth emulator already connected');
  }

  try {
    connectFirestoreEmulator(db, '127.0.0.1', 8080);
    console.log('✅ Firestore emulator connected (port 8080)');
  } catch (e) {
    console.log('⚠️ Firestore emulator already connected');
  }

  try {
    connectStorageEmulator(storage, '127.0.0.1', 9199);
    console.log('✅ Storage emulator connected (port 9199)');
  } catch (e) {
    console.log('⚠️ Storage emulator already connected');
  }

  try {
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
    console.log('✅ Functions emulator connected (port 5001)');
  } catch (e) {
    console.log('⚠️ Functions emulator already connected');
  }

  console.log('🎯 All emulators connected! Your app is using local Firebase services.');
}

// Export the app for other uses
export default app;
